import { useFormik } from "formik";
import * as yup from "yup";
import api from "../config/axiosConfig";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { convertToFormData } from "../config/convertToFormData";
import toast from "react-hot-toast";
import { useRfi } from "../contexts/RfiContext";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/utilityFunctions";
import { actionType, userRoles } from "../utils/constant";

const useRFIAdditions = ({
  showInspectorFields,
  isAdmin,
  stageId,
  isAPICallAllowed,
}) => {
  const rfi_id = useParams().rfinumber;
  const {
    submitStageAPI,
    getRFIStages,
    skipStage,
    getRFIDetails,
    setAdditionsData,
  } = useRfi();
  const { profile } = useSelector((state) => state.profile);

  const [additions, setAdditions] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  // Initial values
  const initialValues = {
    additions: additions,
  };

  // Validation schema
  const validationSchema = yup.object().shape({
    additions: yup.array().of(
      yup.object().shape({
        pin: yup.string().required("Part number is required"),
        part_description: yup.string().required("Description is required"),
        quantity: yup
          .number()
          .typeError("Quantity must be a number")
          .required("Quantity is required")
          .positive("Quantity must be positive"),
        length: yup
          .number()
          .typeError("Length must be a number")
          .required("Length is required")
          .positive("Length must be positive"),
        width: yup
          .number()
          .typeError("Width must be a number")
          .required("Width is required")
          .positive("Width must be positive"),
        part_type: yup.string().required("Part type is required"),
        picture: yup.mixed().nullable(),
        ...((showInspectorFields || isAdmin) && {
          remarks: yup.string().required("Remarks is required"),
          qc_verify: yup.boolean().required("QC verify is required"),
        }),
      })
    ),
  });

  function diffAdditions(additions, valuesAdditions) {
    const updateAddition = [];
    const createAddition = [];
    const excludedKeys = [
      "addition_id",
      "creator",
      "created_at",
      "updated_at",
      "approved_by",
      "created_by",
      "approver",
    ];

    valuesAdditions.forEach((formAddition) => {
      if (formAddition.addition_id) {
        // Find the previous addition by addition_id (assuming additions is an array)
        const prevAddition = additions.find(
          (a) => a.addition_id === formAddition.addition_id
        );

        if (prevAddition) {
          // Get all unique keys from both objects, excluding the specified keys
          const allKeys = new Set([
            ...Object.keys(formAddition).filter(
              (k) => !excludedKeys.includes(k)
            ),
            ...Object.keys(prevAddition).filter(
              (k) => !excludedKeys.includes(k)
            ),
          ]);

          const isDifferent = Array.from(allKeys).some(
            (key) => prevAddition[key] !== formAddition[key]
          );

          if (isDifferent) {
            updateAddition.push(formAddition);
          }
        } else {
          // If addition_id exists but no matching previous addition found, treat as new
          createAddition.push(formAddition);
        }
      } else {
        createAddition.push(formAddition);
      }
    });

    return { updateAddition, createAddition };
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Handle form submission
      try {
        const { updateAddition, createAddition } = diffAdditions(
          additions,
          values.additions
        );

        if (createAddition.length > 0) {
          const picture = createAddition.map(
            (addition) => addition.picture.file
          );
          const desiredFormatData = {
            rfi_id,
            additions: createAddition.map((addition) => {
              let additionData = {
                pin: addition.pin,
                part_description: addition.part_description,
                quantity: addition.quantity,
                length: addition.length,
                width: addition.width,
                part_type: addition.part_type,
                remarks: addition.remarks,
                qc_verify: addition.qc_verify,
              };
              return additionData;
            }),
            pictures: picture,
          };
          const formData = convertToFormData(desiredFormatData);
          await createAdditionAPI(formData);
        }

        if (updateAddition.length > 0) {
          await Promise.all(
            updateAddition.map(async (addition) => {
              const dataToUpdate = {
                pin: addition.pin,
                part_description: addition.part_description,
                quantity: addition.quantity,
                length: addition.length,
                width: addition.width,
                part_type: addition.part_type,
                remarks: addition.remarks,
                qc_verify: addition.qc_verify,
                picture: addition.picture.file,
              };

              const formData = convertToFormData(dataToUpdate);
              await updatedAdditionsAPI(addition.addition_id, formData);
            })
          );
        }
        await getAllAdditionsAPI();
        if (profile?.role !== userRoles.Product_Engineer) {
          await submitStageAPI({ stage_id: stageId });
        }
        await getRFIStages(rfi_id);
        toast.success(
          `Additions ${
            updateAddition.length > 0 ? "updated" : "created"
          } successfully!`
        );
        await getRFIDetails(rfi_id);
      } catch (error) {
        console.log(error);
        toast.error("Failed to update additions!");
      }
    },
  });

  const handleOpenPreview = (index, values) => {
    if (values?.additions?.[index]?.picture?.preview) {
      setPreviewImage(
        values.additions[index].picture.preview ||
          values.additions[index].picture
      );
    } else {
      setPreviewImage(values.additions[index].picture);
    }
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewImage(null);
  };

  const handleAddRow = (values, setFieldValue) => {
    const newPart = {
      pin: "",
      part_description: "",
      quantity: "",
      length: "",
      width: "",
      part_type: "",
      picture: null,
      ...((showInspectorFields || isAdmin) && {
        remarks: "",
        qc_verify: false,
      }),
    };

    setFieldValue("additions", [...values.additions, newPart]);
  };

  const handleDeleteRow = (index, values, setFieldValue, row) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You do not have permission to delete this addition");
      return;
    }
    const updatedAdditions = values.additions.filter((_, i) => i !== index);
    setFieldValue("additions", updatedAdditions);
    if (row.addition_id) {
      deleteRFIAdditionsAPI(row.addition_id);
    }
  };

  const handleImageUpload = (index, event, values, setFieldValue) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You do not have permission to upload this addition");
      return;
    }
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const updatedAdditions = [...values.additions];
        updatedAdditions[index] = {
          ...updatedAdditions[index],
          picture: {
            file,
            preview: e.target.result,
          },
        };
        setFieldValue("additions", updatedAdditions);
      };
      reader.readAsDataURL(file);
    }
  };

  const skipStageFcn = async () => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_skip)) {
      toast.error("You do not have permission to skip this stage");
      return;
    }
    const response = await skipStage({ stage_id: stageId, skip_stage: true });
    if (response.success) {
      toast.success(response?.data?.message || "Stage skipped successfully");
    } else {
      toast.error(response?.error?.message || "Failed to skip stage");
    }
    await getRFIStages(rfi_id);
    await getRFIDetails(rfi_id);
  };

  const createAdditionAPI = async (values) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You do not have permission to create this addition");
      return;
    }
    return await api
      .post(`/api/rfi-additions`, values)
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  };

  const updatedAdditionsAPI = async (additionId, values) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You do not have permission to update this addition");
      return;
    }
    return await api
      .put(`/api/rfi-additions/${additionId}`, values)
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  };

  const deleteRFIAdditionsAPI = async (additionId) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You do not have permission to delete this addition");
      return;
    }
    return await api
      .delete(`/api/rfi-additions/${additionId}`)
      .then(() => {
        toast.success("Addition deleted successfully!");
        return true;
      })
      .catch((err) => {
        console.log(err);
        toast.error("Addition deletion failed!");
        return false;
      });
  };

  const getAllAdditionsAPI = useCallback(async () => {
    return await api
      .get(`/api/rfi-additions/rfi/${rfi_id}`)
      .then((res) => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const formattedData = res.data.map((item) => {
          return {
            ...item,
            picture: item.picture ? `${baseUrl}${item.picture}` : null,
          };
        });
        setAdditions(formattedData);
        setAdditionsData(formattedData);
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  }, [rfi_id, setAdditionsData]);

  useEffect(() => {
    if (!rfi_id || !isAPICallAllowed) return;
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_view)) {
      return;
    }
    getAllAdditionsAPI();
  }, [
    rfi_id,
    getAllAdditionsAPI,
    isAPICallAllowed,
    profile?.permissions?.actions,
  ]);

  return {
    formik,
    previewOpen,
    previewImage,
    additions,
    handleAddRow,
    handleDeleteRow,
    handleImageUpload,
    handleOpenPreview,
    handleClosePreview,
    skipStageFcn,
  };
};

export default useRFIAdditions;
